import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { BarChart3, Users, Shield, GraduationCap, FileText, ChevronDown } from "lucide-react";
import { RelatorioMovimento } from "./RelatorioMovimento";
import { RelatorioMovimentoOficial } from "./RelatorioMovimentoOficial";
import { Patrulhas } from "./Patrulhas";
import { FilasTreinamento } from "./FilasTreinamento";
import { RelatoriosTreinamento } from "./RelatoriosTreinamento";
import { MeuStatusAtual } from "../../components/MeuStatusAtual";
import { usePermissions } from "../../hooks/usePermissions";

export function Central() {
  const [activeTab, setActiveTab] = useState("relatorios");
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const { hasPermission } = usePermissions();

  const canAccessRM = hasPermission("central.rm.gerenciar") || hasPermission("central.rm.gerenciar");
  const canAccessRMO = hasPermission("central.rmo.gerenciar");
  const canAccessPatrulhas = hasPermission("listar_patrulhas") || hasPermission("criar_patrulha");
  const canAccessFilas = true; // Por enquanto, todos têm acesso
  const canAccessRelatorios = hasPermission("listar_relatorios") || hasPermission("editar_relatorio");

  // Se não tem permissão para nada, mostrar mensagem
  if (!canAccessRM && !canAccessRMO && !canAccessPatrulhas && !canAccessFilas && !canAccessRelatorios) {
    return (
      <div className="flex h-[calc(100vh-200px)] items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Sem Permissão</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              Você não tem permissão para acessar a Central.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Central</h1>
        <p className="text-muted-foreground mt-2">
          Gerenciamento de Relatórios, Patrulhas e Filas de Treinamento
        </p>
      </div>

      {/* Meu Status Atual - Sempre visível */}
      <div className="mb-6">
        <MeuStatusAtual />
      </div>

      {/* Mobile nav dropdown */}
      <div className="md:hidden relative mb-4">
        {isMobileNavOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsMobileNavOpen(false)}
          />
        )}
        <button
          onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-muted rounded-lg border text-sm font-medium"
        >
          <span className="flex items-center gap-2">
            {activeTab === "relatorios" && <><FileText className="h-4 w-4" /> Relatórios</>}
            {activeTab === "filas" && <><GraduationCap className="h-4 w-4" /> Filas</>}
            {activeTab === "patrulhas" && <><Shield className="h-4 w-4" /> Patrulhas</>}
            {activeTab === "rm" && <><BarChart3 className="h-4 w-4" /> RM</>}
            {activeTab === "rmo" && <><Users className="h-4 w-4" /> RMO</>}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isMobileNavOpen ? "rotate-180" : ""}`} />
        </button>
        {isMobileNavOpen && (
          <div className="relative z-50 mt-1 w-full bg-background border rounded-lg shadow-lg overflow-hidden">
            {canAccessRelatorios && (
              <button
                onClick={() => { setActiveTab("relatorios"); setIsMobileNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted transition-colors ${activeTab === "relatorios" ? "bg-muted font-medium" : ""}`}
              >
                <FileText className="h-4 w-4" /> Relatórios
              </button>
            )}
            {canAccessFilas && (
              <button
                onClick={() => { setActiveTab("filas"); setIsMobileNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted transition-colors ${activeTab === "filas" ? "bg-muted font-medium" : ""}`}
              >
                <GraduationCap className="h-4 w-4" /> Filas
              </button>
            )}
            {canAccessPatrulhas && (
              <button
                onClick={() => { setActiveTab("patrulhas"); setIsMobileNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted transition-colors ${activeTab === "patrulhas" ? "bg-muted font-medium" : ""}`}
              >
                <Shield className="h-4 w-4" /> Patrulhas
              </button>
            )}
            {canAccessRM && (
              <button
                onClick={() => { setActiveTab("rm"); setIsMobileNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted transition-colors ${activeTab === "rm" ? "bg-muted font-medium" : ""}`}
              >
                <BarChart3 className="h-4 w-4" /> RM
              </button>
            )}
            {canAccessRMO && (
              <button
                onClick={() => { setActiveTab("rmo"); setIsMobileNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted transition-colors ${activeTab === "rmo" ? "bg-muted font-medium" : ""}`}
              >
                <Users className="h-4 w-4" /> RMO
              </button>
            )}
          </div>
        )}
      </div>

      {/* Desktop tabs */}
      <div className="hidden md:flex border-b mb-6 gap-1">
        {canAccessRelatorios && (
          <button
            onClick={() => setActiveTab("relatorios")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "relatorios" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"}`}
          >
            <FileText className="h-4 w-4" /> Relatórios
          </button>
        )}
        {canAccessFilas && (
          <button
            onClick={() => setActiveTab("filas")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "filas" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"}`}
          >
            <GraduationCap className="h-4 w-4" /> Filas
          </button>
        )}
        {canAccessPatrulhas && (
          <button
            onClick={() => setActiveTab("patrulhas")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "patrulhas" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"}`}
          >
            <Shield className="h-4 w-4" /> Patrulhas
          </button>
        )}
        {canAccessRM && (
          <button
            onClick={() => setActiveTab("rm")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "rm" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"}`}
          >
            <BarChart3 className="h-4 w-4" /> RM
          </button>
        )}
        {canAccessRMO && (
          <button
            onClick={() => setActiveTab("rmo")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "rmo" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"}`}
          >
            <Users className="h-4 w-4" /> RMO
          </button>
        )}
      </div>

      {/* Tab content */}
      <div>
        {canAccessRelatorios && activeTab === "relatorios" && <RelatoriosTreinamento />}
        {canAccessFilas && activeTab === "filas" && <FilasTreinamento />}
        {canAccessPatrulhas && activeTab === "patrulhas" && <Patrulhas />}
        {canAccessRM && activeTab === "rm" && <RelatorioMovimento />}
        {canAccessRMO && activeTab === "rmo" && <RelatorioMovimentoOficial />}
      </div>
    </div>
  );
}
